import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn, getInitials } from '@/lib/utils'
import { Settings, ChevronLeft, ChevronRight } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { useOrganizationStore } from '@/stores/organization-store'
import { useAtendimento } from '@/hooks/use-atendimento'
import { Button } from '@/components/ui/button'
import { getMenuItems } from './menu-config'
import { SpartanHelmet } from '@/components/icons/spartan-helmet'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { user } = useAuthStore()
  const { currentOrganization } = useOrganizationStore()
  const { temModulo } = useAtendimento(currentOrganization?.id)
  // `temModulo` é `null` enquanto a resposta não chega, e `null` esconde: o
  // grupo aparecendo e sumindo a cada carregamento é pior que aparecer tarde.
  const menuItems = getMenuItems(t).filter(
    (item) => !item.requiresAtendimento || temModulo === true
  )

  return (
    <aside
      className={cn(
        'bg-[#0d0d14] border-r border-gray-800/50 flex flex-col sticky top-0 h-screen transition-all duration-300 relative',
        'hidden md:flex', // Hide on mobile, show on tablet+
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Collapse Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggle}
        className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border border-gray-700 bg-[#0d0d14] text-gray-400 hover:text-white hover:bg-gray-800"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>

      {/* Logo */}
      <div className="h-16 flex-shrink-0 flex items-center px-6 border-b border-gray-800/50">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-glow-primary">
            <SpartanHelmet className="w-6 h-6 text-white" />
          </div>
          {!collapsed && (
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              ARCHON
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin">
        {menuItems
          .filter((item) => !item.requiresSuperadmin || user?.is_superadmin)
          .map((item, indice, visiveis) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            // O TÍTULO DO GRUPO SAI UMA VEZ, acima do primeiro item dele.
            const abreGrupo =
              !!item.group && item.group !== visiveis[indice - 1]?.group

            return (
              <div key={`grupo-${item.id}`}>
              {abreGrupo && !collapsed && (
                <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-600">
                  {item.group}
                </p>
              )}
              {abreGrupo && collapsed && (
                <div className="mx-3 my-3 border-t border-gray-800/50" />
              )}
              <Link
                to={item.path}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-xl',
                  'transition-all duration-200 group relative',
                  isActive
                    ? 'bg-indigo-500/20 text-indigo-300'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                )}
                title={collapsed ? item.label : undefined}
              >
                {isActive && (
                  <div className="absolute left-0 w-1 h-8 bg-indigo-500 rounded-r-full" />
                )}
                <item.icon
                  className={cn(
                    'w-5 h-5 flex-shrink-0',
                    isActive && 'text-indigo-400'
                  )}
                />
                {!collapsed && (
                  <span className="font-medium truncate">{item.label}</span>
                )}
                {item.badge && (
                  <span
                    className={cn(
                      'bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center',
                      collapsed ? 'absolute -top-1 -right-1 px-1' : 'ml-auto px-2'
                    )}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
              </div>
            )
          })}
      </nav>

      {/* Bottom section - Settings + User */}
      <div className="flex-shrink-0 mt-auto">
        {/* Settings */}
        <div className="px-3 pb-2">
          <Link
            to="/settings"
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-xl',
              'text-gray-400 hover:bg-white/5 hover:text-gray-200 transition-colors'
            )}
            title={collapsed ? t('nav.settings') : undefined}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="font-medium">{t('nav.settings')}</span>}
          </Link>
        </div>

        {/* User */}
        <div className="p-4 border-t border-gray-800/50">
        <Link
          to="/profile"
          className={cn(
            'flex items-center rounded-xl p-2 -m-2 transition-colors hover:bg-white/5',
            collapsed ? 'justify-center' : 'gap-3'
          )}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-white">
              {getInitials(user?.full_name, user?.email)}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-gray-200">
                {user?.full_name || t('header.user')}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          )}
        </Link>
        </div>
      </div>
    </aside>
  )
}
